import React, { useState } from "react";
import { FileSearch, Camera, Sparkles, Clock, MoreHorizontal, X } from "lucide-react";

/**
 * MobileBottomNav. case-detail navigation pattern for phones.
 *
 * Replaces the 9-tab horizontal scroller (overwhelming on a 390px screen)
 * with an app-like fixed bottom nav containing the 4 core tabs + "More"
 * which opens a bottom-sheet drawer with everything else.
 *
 * Hidden ≥ md (768px). desktop continues to use the existing tab bar.
 *
 * Props:
 * tab. current active tab key
 * setTab. fn(key) to switch tabs
 * secondaryTabs. array of [key, label, Icon] for the More menu
 */
const CORE_TABS = [
 ["documents", "Docs", FileSearch],
 ["photo-trail", "Photos", Camera],
 ["chat", "Compass", Sparkles],
 ["timeline", "Timeline", Clock],
];

export default function MobileBottomNav({ tab, setTab, secondaryTabs = [] }) {
 const [moreOpen, setMoreOpen] = useState(false);

 // If the active tab is one of the secondary ones, light up "More" in the bottom nav
 const activeIsSecondary = secondaryTabs.some(([k]) => k === tab);

 const pick = (k) => {
 setTab(k);
 setMoreOpen(false);
 };

 return (
 <>
 <nav className="mobile-bottom-nav" role="navigation" aria-label="Case sections" data-testid="mobile-bottom-nav">
 {CORE_TABS.map(([k, label, Icon]) => (
 <button
 key={k}
 onClick={() => pick(k)}
 className={`mobile-bottom-nav-item ${tab === k ? "active" : ""}`}
 data-testid={`mobile-tab-${k}`}
 aria-current={tab === k ? "page" : undefined}
 >
 <Icon size={20} strokeWidth={1.6} />
 <span>{label}</span>
 </button>
 ))}
 <button
 onClick={() => setMoreOpen(true)}
 className={`mobile-bottom-nav-item ${activeIsSecondary ? "active" : ""}`}
 data-testid="mobile-tab-more"
 aria-haspopup="dialog"
 aria-expanded={moreOpen}
 >
 <MoreHorizontal size={20} strokeWidth={1.6} />
 <span>More</span>
 </button>
 </nav>

 {/* Bottom-sheet drawer for secondary tabs + Inspections (which is core but doesn't fit in 5 slots) */}
 {moreOpen && (
 <div className="fixed inset-0 z-50 flex items-end" data-testid="mobile-more-drawer">
 <button
 className="absolute inset-0 bg-black/30"
 onClick={() => setMoreOpen(false)}
 aria-label="Close menu"
 />
 <div className="relative w-full bg-[#FAF9F6] rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)] max-h-[80vh] overflow-y-auto">
 <div className="flex justify-center py-2">
 <div className="w-10 h-1 bg-[#D6D5CE] rounded-full" />
 </div>
 <div className="flex items-center justify-between px-5 pb-2">
 <h3 className="font-serif text-lg text-[#0D2B52]">More sections</h3>
 <button onClick={() => setMoreOpen(false)} className="tap-target text-[#6B7280]" data-testid="mobile-more-close">
 <X size={18} strokeWidth={1.5} />
 </button>
 </div>
 <div className="divide-y divide-[#EFEDE5]">
 {/* Inspections is core-ish, lives here on mobile to keep core nav to 5 slots */}
 <button
 onClick={() => pick("inspections")}
 className="w-full flex items-center gap-3 px-5 py-4 text-left tap-target hover:bg-[#EFEDE5]/50"
 data-testid="mobile-more-inspections"
 >
 <FileSearch size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
 <span className="font-serif text-[15px] text-[#0D2B52]">Inspections</span>
 {tab === "inspections" && <span className="ml-auto text-[10px] font-mono text-[#1B6C62] uppercase">Current</span>}
 </button>
 {secondaryTabs.map(([k, label, Icon]) => (
 <button
 key={k}
 onClick={() => pick(k)}
 className="w-full flex items-center gap-3 px-5 py-4 text-left tap-target hover:bg-[#EFEDE5]/50"
 data-testid={`mobile-more-${k}`}
 >
 <Icon size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
 <span className="font-serif text-[15px] text-[#0D2B52]">{label}</span>
 {tab === k && <span className="ml-auto text-[10px] font-mono text-[#1B6C62] uppercase">Current</span>}
 </button>
 ))}
 </div>
 </div>
 </div>
 )}
 </>
 );
}
